import React, { useState, useCallback } from 'react';
import './StageVisualizer.css';
import gsap from 'gsap';

const StageVisualizer = ({ stage, description, data }) => {
  // Stages that happen in the cloud (after request is sent)
  const cloudStages = ['tokenization', 'embedding', 'rag_retrieval', 'context_assembly', 'llm_inference', 'token_generation'];
  const isCloudStage = cloudStages.includes(stage);

  const renderStageAnimation = () => {
    switch (stage) {
      case 'request':
        return (
          <div className="stage-animation-content">
            <div className="info-box-left">
              {data?.transformation && (
                <div className="transformation-info">
                <div className="transform-label">📨 Request Received</div>
                <div className="transform-text">{data.transformation}</div>
                {data?.input && (
                  <div className="transform-detail">
                    <strong>User Query:</strong><br/>{data.input.substring(0, 80)}{data.input.length > 80 ? '...' : ''}
                  </div>
                )}
                {data?.output && (
                  <div className="transform-detail">
                    <strong>Status:</strong> {data.output}
                  </div>
                )}
                <div className="transform-detail">
                  <strong>Destination:</strong> Cloud AI Processing
                </div>
              </div>
            )}
            </div>
            <div className="animation-wrapper">
              <div className="phone-icon">
                <div className="phone-body">
                  <div className="phone-screen">
                    <div className="question-text">{data?.query ? data.query.substring(0, 50) + (data.query.length > 50 ? '...' : '') : '?'}</div>
                  </div>
                </div>
              </div>
              <div className="signal-waves">
                <div className="wave wave-1"></div>
                <div className="wave wave-2"></div>
                <div className="wave wave-3"></div>
                <div className="data-arrow">→</div>
              </div>
              <div className="cloud-icon">
                <div className="cloud cloud-main"></div>
                <div className="cloud cloud-small"></div>
              </div>
            </div>
          </div>
        );

      case 'tokenization':
        const tokens = data?.tokens || ['Your', 'Question'];
        return (
          <div className="stage-animation-content">
            <div className="info-box-left">
              {data?.transformation && (
                <div className="transformation-info">
                <div className="transform-label">🔄 {data?.tokenizer ? `Tokenized via ${data.tokenizer}` : 'Transformation'}</div>
                <div className="transform-text">{data.transformation}</div>
                <div className="transform-process">{data.process}</div>
                {data?.input && (
                  <div className="transform-detail">
                    <strong>Input:</strong> {data.input.substring(0, 80)}{data.input.length > 80 ? '...' : ''}
                  </div>
                )}
                {data?.output && (
                  <div className="transform-detail">
                    <strong>Output:</strong> {data.output.substring(0, 80)}{data.output.length > 80 ? '...' : ''}
                  </div>
                )}
                {data?.token_count && (
                  <div className="transform-detail">
                    <strong>Token Count:</strong> {data.token_count}
                  </div>
                )}
                {data?.token_ids && (
                  <div className="transform-detail">
                    <strong>Token IDs:</strong> [{data.token_ids.slice(0, 10).join(', ')}{data.token_ids.length > 10 ? ', ...' : ''}]
                  </div>
                )}
              </div>
            )}
            </div>
            <div className="animation-wrapper">
              <div className="text-container">
                <div className="text-whole">{data?.query || 'Your Question'}</div>
                <div className="breaking-line"></div>
              </div>
              <div className="tokens-container">
                {tokens.slice(0, 15).map((token, idx) => (
                  <div key={idx} className={`token token-${(idx % 4) + 1}`} style={{ animationDelay: `${idx * 0.1}s` }}>
                    {token}
                  </div>
                ))}
                {tokens.length > 15 && <div className="token">... +{tokens.length - 15} more</div>}
              </div>
              <div className="cloud-icon">
                <div className="cloud cloud-main"></div>
                <div className="cloud cloud-small"></div>
              </div>
            </div>
          </div>
        );

      case 'embedding':
        const embedTokens = data?.tokens?.slice(0, 5) || ['token1', 'token2', 'token3'];
        const embeddingPreview = data?.embedding_preview || data?.embeddings || [0.012, -0.034, 0.056, -0.023, 0.045];
        const embModel = data?.model || 'text-embedding-3-small';
        const vecDims = data?.vector_dims || 1536;
        return (
          <div className="stage-animation-content">
            <div className="info-box-left">
              {data?.transformation && (
                <div className="transformation-info">
                <div className="transform-label">🔄 Real Embedding via OpenAI</div>
                <div className="transform-text">{data.transformation}</div>
                <div className="transform-process">{data.process}</div>
                {data?.model && (
                  <div className="transform-detail">
                    <strong>Model:</strong> {data.model}
                  </div>
                )}
                {data?.vector_dims && (
                  <div className="transform-detail">
                    <strong>Dimensions:</strong> {data.vector_dims}D vector space
                  </div>
                )}
                {data?.input && (
                  <div className="transform-detail">
                    <strong>Input:</strong> {data.input}
                  </div>
                )}
                {data?.output && (
                  <div className="transform-detail">
                    <strong>Output:</strong> {data.output}
                  </div>
                )}
              </div>
            )}
            </div>
            <div className="animation-wrapper">
              <div className="vector-space">
                {/* Floating vector dot animations */}
                {embedTokens.slice(0, 5).map((tok, idx) => (
                  <div key={idx} className={`vector vector-${(idx % 3) + 1}`} style={{ animationDelay: `${idx * 0.3}s` }}>
                    <span className="vector-label">{tok}</span>
                  </div>
                ))}
                {/* Real embedding numbers */}
                <div className="real-embedding-display">
                  <div className="embedding-model-label">📡 {embModel}</div>
                  <div className="embedding-vector-preview">
                    [{Array.isArray(embeddingPreview) ? embeddingPreview.map((v, i) => (
                      <span key={i} className="embed-value" style={{ animationDelay: `${i * 0.15}s` }}>
                        {typeof v === 'number' ? v.toFixed(4) : v}{i < embeddingPreview.length - 1 ? ', ' : ''}
                      </span>
                    )) : embeddingPreview}, ...]
                  </div>
                  <div className="embedding-dims-label">{vecDims} dimensions total</div>
                </div>
                <div className="grid-background">
                  <div className="grid-line grid-h1"></div>
                  <div className="grid-line grid-h2"></div>
                  <div className="grid-line grid-v1"></div>
                  <div className="grid-line grid-v2"></div>
                </div>
              </div>
              <div className="cloud-icon">
                <div className="cloud cloud-main"></div>
                <div className="cloud cloud-small"></div>
              </div>
            </div>
          </div>
        );

      case 'rag_retrieval':
        const retrievedDocs = data?.retrieved_docs || [];
        const docsSearched = data?.docs_searched || 0;
        const docsRetrieved = data?.docs_retrieved || retrievedDocs.length;
        return (
          <div className="stage-animation-content">
            <div className="info-box-left">
              <div className="search-stats">
                <div className="transform-label">📊 Cloud Vector DB Search</div>
                <div className="stat-item">
                  🔍 <strong>{docsSearched.toLocaleString()}</strong> documents searched
                </div>
                <div className="stat-item">
                  ✅ <strong>{docsRetrieved}</strong> relevant matches found
                </div>
                {data?.transformation && (
                  <>
                    <div className="transform-text" style={{marginTop: '8px'}}>{data.transformation}</div>
                    <div className="transform-process">{data.process}</div>
                  </>
                )}
                {data?.query && (
                  <div className="transform-detail" style={{marginTop: '6px'}}>
                    <strong>Query:</strong> "{data.query}"
                  </div>
                )}
              </div>
            </div>
            <div className="animation-wrapper">
              <div className="books-container">
                <div className="book book-1">
                  <div className="book-cover"></div>
                  <div className="book-pages"></div>
                </div>
                <div className="book book-2">
                  <div className="book-cover"></div>
                  <div className="book-pages"></div>
                </div>
                <div className="book book-3">
                  <div className="book-cover"></div>
                  <div className="book-pages"></div>
                </div>
              </div>
              <div className="search-beam"></div>
              {retrievedDocs.length > 0 && (
                <div className="retrieved-docs">
                  <div className="retrieved-docs-title">📄 Retrieved Knowledge for Your Question:</div>
                  {retrievedDocs.map((doc, idx) => (
                    <div key={idx} className="doc-item" style={{ animationDelay: `${idx * 0.5}s` }}>
                      <span className="doc-score">#{idx + 1}</span> {doc}
                    </div>
                  ))}
                </div>
              )}
              <div className="cloud-icon">
                <div className="cloud cloud-main"></div>
                <div className="cloud cloud-small"></div>
              </div>
            </div>
          </div>
        );

      case 'context_assembly':
        const contextDocs = data?.context_docs || [];
        const queryWords = data?.query ? data.query.split(' ').slice(0, 5).join(' ') : 'Question';
        const promptPreview = data?.assembled_preview || '';
        const promptTokens = data?.prompt_token_count || 0;
        return (
          <div className="stage-animation-content">
            <div className="info-box-left">
              {data?.transformation && (
                <div className="transformation-info">
                <div className="transform-label">🔄 Prompt Assembly</div>
                <div className="transform-text">{data.transformation}</div>
                <div className="transform-process">{data.process}</div>
                {data?.input && (
                  <div className="transform-detail">
                    <strong>Input:</strong> {data.input}
                  </div>
                )}
                {data?.output && (
                  <div className="transform-detail">
                    <strong>Output:</strong> {data.output}
                  </div>
                )}
                {promptTokens > 0 && (
                  <div className="transform-detail">
                    <strong>Prompt Tokens:</strong> {promptTokens}
                  </div>
                )}
                {data?.context_length && (
                  <div className="transform-detail">
                    <strong>Context Length:</strong> {data.context_length} characters
                  </div>
                )}
              </div>
            )}
            </div>
            <div className="animation-wrapper">
              <div className="puzzle-container">
                <div className="puzzle-piece piece-1">
                  <div className="piece-content">Q: {queryWords}...</div>
                </div>
                <div className="puzzle-piece piece-2">
                  <div className="piece-content">Knowledge: {contextDocs.length} docs</div>
                </div>
                <div className="puzzle-piece piece-3">
                  <div className="piece-content">Context</div>
                </div>
                <div className="assembled-indicator">✓</div>
              </div>
              {promptPreview && (
                <div className="assembled-prompt-preview">
                  <div className="prompt-preview-title">📝 Assembled Prompt Sent to LLM:</div>
                  <div className="prompt-preview-text">{promptPreview}</div>
                </div>
              )}
              <div className="cloud-icon">
                <div className="cloud cloud-main"></div>
                <div className="cloud cloud-small"></div>
              </div>
            </div>
          </div>
        );

      case 'llm_inference':
        return (
          <div className="stage-animation-content">
            <div className="info-box-left">
              {data?.transformation && (
                <div className="transformation-info">
                <div className="transform-label">🧠 LLM Inference on OpenAI Cloud</div>
                <div className="transform-text">{data.transformation}</div>
                <div className="transform-process">{data.process}</div>
                {data?.model && (
                  <div className="transform-detail">
                    <strong>Model:</strong> {data.model}
                  </div>
                )}
                {data?.prompt_tokens && (
                  <div className="transform-detail">
                    <strong>Prompt Tokens:</strong> {data.prompt_tokens}
                  </div>
                )}
                {data?.input && (
                  <div className="transform-detail">
                    <strong>Input:</strong> {data.input}
                  </div>
                )}
                {data?.output && (
                  <div className="transform-detail">
                    <strong>Output:</strong> {data.output}
                  </div>
                )}
              </div>
            )}
            </div>
            <div className="animation-wrapper">
              <div className="brain-container">
                <div className="brain">
                  <div className="brain-lobe brain-left"></div>
                  <div className="brain-lobe brain-right"></div>
                </div>
                <div className="neural-connections">
                  <div className="neuron neuron-1">
                    <div className="pulse"></div>
                  </div>
                  <div className="neuron neuron-2">
                    <div className="pulse"></div>
                  </div>
                  <div className="neuron neuron-3">
                    <div className="pulse"></div>
                  </div>
                  <div className="connection-line line-1"></div>
                  <div className="connection-line line-2"></div>
                  <div className="connection-line line-3"></div>
                </div>
              </div>
              <div className="cloud-icon">
                <div className="cloud cloud-main"></div>
                <div className="cloud cloud-small"></div>
              </div>
              {data?.query && (
                <div className="processing-query">
                  Processing: "{data.query.substring(0, 50)}{data.query.length > 50 ? '...' : ''}"
                </div>
              )}
            </div>
          </div>
        );

      case 'token_generation':
        const answerText = data?.answer_text || '';
        return (
          <div className="stage-animation-content token-gen-layout">
            <div className="typing-header">
              <h3>🤖 AI is answering your question...</h3>
            </div>
            <div className="typing-container-large">
              <div className="answer-text-display-large">
                {answerText || 'Starting to generate answer...'}
                <span className="cursor-blink"></span>
              </div>
            </div>
            <div className="cloud-stage-indicator">
              <div className="cloud-bg cloud-1"></div>
              <div className="cloud-bg cloud-2"></div>
              <div className="processing-label">☁️ Cloud</div>
            </div>
          </div>
        );

      case 'response':
        return (
          <ResponseStageAnimation data={data} />
        );

      case 'error':
        console.log('=== ERROR STAGE ===');
        console.log('Error:', data?.error);
        console.log('Error type:', data?.error_type);
        console.log('===================');
        return (
          <div className="stage-animation-content response-layout">
            <div className="error-header" style={{textAlign: 'center'}}>
              <div style={{fontSize: '80px', marginBottom: '20px'}}>❌</div>
              <h2 style={{color: '#ff6b9d', fontSize: '32px'}}>Error Occurred</h2>
            </div>
            <div className="final-answer-box" style={{borderColor: 'rgba(255, 107, 157, 0.6)'}}>
              <h3 style={{color: '#ff6b9d'}}>⚠️ Error Details:</h3>
              <div className="answer-content" style={{background: 'rgba(255, 0, 0, 0.1)'}}>
                <strong>Type:</strong> {data?.error_type || 'Unknown'}<br/><br/>
                <strong>Message:</strong> {data?.error || 'Unknown error'}<br/><br/>
                <strong>Description:</strong> {data?.description || 'No description available'}
              </div>
              <div style={{marginTop: '20px', padding: '15px', background: 'rgba(100, 200, 255, 0.1)', borderRadius: '8px', fontSize: '14px'}}>
                <strong>💡 Common Solutions:</strong><br/>
                • Check your OpenAI API key in backend/.env<br/>
                • Ensure you have API credits available<br/>
                • Check the backend terminal for detailed error logs<br/>
                • Verify your internet connection
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const stageInfo = {
    request: { title: '📱 Request Captured', emoji: '📱' },
    tokenization: { title: '🔤 Tokenization', emoji: '🔤' },
    embedding: { title: '🧮 Vector Embedding', emoji: '🧮' },
    rag_retrieval: { title: '📚 Knowledge Retrieval', emoji: '📚' },
    context_assembly: { title: '⚡ Context Assembly', emoji: '⚡' },
    llm_inference: { title: '🧠 LLM Processing', emoji: '🧠' },
    token_generation: { title: '💬 Generating Response', emoji: '💬' },
    response: { title: '', emoji: '✅' },
    error: { title: '❌ Error', emoji: '❌' },
  };

  if (!stage || !stageInfo[stage]) return null;

  return (
    <div className="stage-animation">
      <div className="stage-title">
        {stageInfo[stage].title}
      </div>
      <div className="stage-description">
        {description}
      </div>
      <div className={`stage-visual ${isCloudStage ? 'cloud-stage' : ''}`}>
        {renderStageAnimation()}
      </div>
    </div>
  );
};

// Pipeline Animation for Response Stage (Pure CSS) — with clickable stage details
const ResponseStageAnimation = ({ data }) => {
  const [selectedStage, setSelectedStage] = useState(null);
  const [stageExplanation, setStageExplanation] = useState('');
  const [loadingDetail, setLoadingDetail] = useState(false);

  const stages = [
    { id: 1, key: 'request', icon: '📱', name: 'Question', color: '#ff6b50', isCloud: false },
    { id: 2, key: 'tokenization', icon: '🔤', name: 'Tokenize', color: '#feca57', isCloud: true },
    { id: 3, key: 'embedding', icon: '🧬', name: 'Embed', color: '#00d2d3', isCloud: true },
    { id: 4, key: 'rag_retrieval', icon: '☁️', name: 'Retrieve', color: '#ff9ff3', isCloud: true },
    { id: 5, key: 'context_assembly', icon: '🔄', name: 'Assemble', color: '#54a0ff', isCloud: true },
    { id: 6, key: 'llm_inference', icon: '🧠', name: 'LLM', color: '#48dbfb', isCloud: true },
    { id: 7, key: 'token_generation', icon: '✨', name: 'Answer', color: '#1dd1a1', isCloud: true }
  ];

  const handleStageClick = useCallback(async (stage) => {
    // If clicking the same stage, close it
    if (selectedStage?.id === stage.id) {
      setSelectedStage(null);
      setStageExplanation('');
      return;
    }

    setSelectedStage(stage);
    setStageExplanation('');
    setLoadingDetail(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL
        ? `${process.env.REACT_APP_API_URL}/api/stage-details`
        : '/api/stage-details';

      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: stage.key, query: data?.query || '' }),
      });

      const result = await res.json();
      setStageExplanation(result.explanation || 'No details available.');
    } catch (err) {
      setStageExplanation('Could not load stage details. Please try again.');
    } finally {
      setLoadingDetail(false);
    }
  }, [selectedStage, data?.query]);

  const closeModal = useCallback(() => {
    setSelectedStage(null);
    setStageExplanation('');
  }, []);

  return (
    <div className="stage-animation-content response-layout">
      <div className="pipeline-wrapper">
        <h2 className="pipeline-title">📊 Your Question's Journey Through the Cloud</h2>
        <p className="pipeline-tap-hint">👆 Tap any stage to learn what happens in the cloud!</p>

        {/* Pipeline Track */}
        <div className="pipeline-track">
          <div className="pipeline-line">
            <div className="pipeline-pulse"></div>
          </div>
          {stages.map((stage, idx) => (
            <div
              key={stage.id}
              className={`pipeline-node clickable-node ${selectedStage?.id === stage.id ? 'node-selected' : ''}`}
              style={{
                '--node-color': stage.color,
                '--delay': `${idx * 0.3}s`
              }}
              onClick={() => handleStageClick(stage)}
              title={`Click to learn about ${stage.name}`}
            >
              <div className="node-circle">
                <span className="node-emoji">{stage.icon}</span>
                <div className="node-ring"></div>
                {stage.isCloud && <div className="cloud-badge">☁️</div>}
              </div>
              <div className="node-label">{stage.name}</div>
              {idx < stages.length - 1 && (
                <div className="node-arrow">
                  <div className="arrow-dot"></div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Stage Detail Modal */}
        {selectedStage && (
          <div className="stage-detail-modal">
            <div className="stage-detail-header">
              <span className="stage-detail-emoji">{selectedStage.icon}</span>
              <span className="stage-detail-name">{selectedStage.name}</span>
              {selectedStage.isCloud ? (
                <span className="stage-detail-badge cloud-badge-label">☁️ Cloud</span>
              ) : (
                <span className="stage-detail-badge local-badge-label">📱 Local</span>
              )}
              <button className="stage-detail-close" onClick={closeModal}>✕</button>
            </div>
            <div className="stage-detail-body">
              {loadingDetail ? (
                <div className="stage-detail-loading">
                  <div className="detail-spinner"></div>
                  <span>Asking AI to explain this stage...</span>
                </div>
              ) : (
                <p className="stage-detail-text">{stageExplanation}</p>
              )}
            </div>
          </div>
        )}

        {/* Q&A Display */}
        <div className="qa-display">
          <div className="qa-box qa-question">
            <div className="qa-badge">Q</div>
            <div className="qa-content">{data?.query?.substring(0, 80) || 'Your question...'}</div>
          </div>
          <div className="qa-arrow-down">▼</div>
          <div className="qa-box qa-answer">
            <div className="qa-badge qa-badge-answer">A</div>
            <div className="qa-content">{data?.final_answer || 'AI answer...'}</div>
          </div>
        </div>

        <div className="pipeline-footer">
          <span>🎉</span>
          <span>Processed through 7 AI stages!</span>
        </div>
      </div>

      <div className="cloud-role-explanation"></div>
    </div>
  );
};

export default StageVisualizer;
